import { create } from 'zustand';
import { ClientOrderDetailDTO, ClientOrderDTO, OrderStatus } from '@api/generated/search-service';
import { clientOrderControllerApi, workflowApi } from '@api/api-config';
import { useOrgPortal } from '@hooks/useOrgPortal.ts';

interface OrderFilters {
  page?: number;
  size?: number;
  query?: string;
  orderStatus?: OrderStatus;
  dateFrom?: string;
  dateTo?: string;
}

export interface WorkflowAction {
  actionId: string;
  label: string;
}

interface OrderState {
  orders: ClientOrderDTO[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  pageSize: number;
  isLoading: boolean;
  error: string | null;
  filters: OrderFilters;

  // Detail state
  orderDetail: ClientOrderDetailDTO | null;
  isLoadingDetail: boolean;
  errorDetail: string | null;

  // Workflow state
  workflowActions: WorkflowAction[];
  isLoadingActions: boolean;

  // Actions
  fetchOrders: (filters?: Partial<OrderFilters>) => Promise<void>;
  fetchOrderDetail: (orderId: number) => Promise<void>;
  setFilters: (filters: Partial<OrderFilters>) => void;
  finishOrder: (orderId: number) => Promise<void>;
  cancelOrder: (orderId: number) => Promise<void>;
  fetchWorkflowActions: (workflowInstanceId: number) => Promise<void>;
  completeWorkflowAction: (workflowInstanceId: number, actionId: string, comment?: string) => Promise<void>;
  clearFilters: () => void;
  clearError: () => void;
  clearDetail: () => void;
}

const defaultFilters: OrderFilters = {
  page: 1,
  size: 20,
};

export const useOrderStore = create<OrderState>(set => ({
  orders: [],
  totalElements: 0,
  totalPages: 0,
  currentPage: 1,
  pageSize: 20,
  isLoading: false,
  error: null,
  filters: defaultFilters,
  orderDetail: null,
  isLoadingDetail: false,
  errorDetail: null,

  // Workflow state
  workflowActions: [],
  isLoadingActions: false,

  finishOrder: async (orderId: number) => {
    try {
      const portalId = useOrgPortal.getState().portalId;
      const organizationId = useOrgPortal.getState().portalId;

      if (!organizationId || !portalId) {
        throw new Error('Organizace nebo portál nebyl nalezen');
      }

      await clientOrderControllerApi.finishOrder({
        organizationId,
        portalId,
        orderId,
      });

      // Znovu načteme detail objednávky
      await useOrderStore.getState().fetchOrderDetail(orderId);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Nepodařilo se dokončit objednávku';
      throw new Error(errorMessage);
    }
  },
  cancelOrder: async (orderId: number) => {
    try {
      const { orgId: organizationId, portalId } = useOrgPortal.getState();

      if (!organizationId || !portalId) {
        throw new Error('Organizace nebo portál nebyl nalezen');
      }

      await clientOrderControllerApi.cancelOrder({
        organizationId,
        portalId,
        orderId,
      });

      // Znovu načteme detail objednávky
      await useOrderStore.getState().fetchOrderDetail(orderId);
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Nepodařilo se zrušit objednávku';
      throw new Error(errorMessage);
    }
  },
  fetchOrders: async (newFilters?: Partial<OrderFilters>) => {
    set({ isLoading: true, error: null });

    try {
      const portalId = useOrgPortal.getState().portalId;
      const organizationId = useOrgPortal.getState().orgId;

      if (!organizationId || !portalId) {
        set({ error: 'Organizace nebo portál nebyl nalezen', isLoading: false });
        return;
      }

      // Aktualizujeme filtry pokud byly předány
      if (newFilters) {
        set(state => ({
          filters: { ...state.filters, ...newFilters },
        }));
      }

      // Získáme aktuální filtry
      const currentState = useOrderStore.getState();
      const filters = { ...currentState.filters, ...newFilters };

      const response = await clientOrderControllerApi.getOrders({
        organizationId,
        portalId,
        page: filters.page,
        size: filters.size,
        query: filters.query,
        orderStatus: filters.orderStatus,
        dateFrom: filters.dateFrom,
        dateTo: filters.dateTo,
      });

      set({
        orders: response.data?.orders || [],
        totalElements: response.data?.totalElements || 0,
        totalPages: response.data?.totalPages || 0,
        currentPage: response.data?.currentPage || 1,
        pageSize: response.data?.pageSize || 20,
        isLoading: false,
        error: null,
      });
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Nepodařilo se načíst objednávky';
      set({
        error: errorMessage,
        isLoading: false,
      });
    }
  },

  setFilters: (newFilters: Partial<OrderFilters>) => {
    set(state => ({
      filters: { ...state.filters, ...newFilters, page: 1 }, // Reset na první stránku při změně filtrů
    }));
  },

  clearFilters: () => {
    set({ filters: defaultFilters });
  },

  clearError: () => {
    set({ error: null });
  },

  fetchOrderDetail: async (orderId: number) => {
    set({ isLoadingDetail: true, errorDetail: null });

    try {
      const { orgId: organizationId, portalId } = useOrgPortal.getState();

      if (!organizationId || !portalId) {
        set({ errorDetail: 'Organizace nebo portál nebyl nalezen', isLoadingDetail: false });
        return;
      }

      const response = await clientOrderControllerApi.getOrderDetail({
        organizationId,
        portalId,
        orderId,
      });

      set({
        orderDetail: response.data || null,
        isLoadingDetail: false,
        errorDetail: null,
      });

      // Pokud má workflow, načíst dostupné akce
      const workflowInstanceId = response.data?.workflowInstanceId;
      if (workflowInstanceId) {
        await useOrderStore.getState().fetchWorkflowActions(workflowInstanceId);
      } else {
        set({ workflowActions: [] });
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Nepodařilo se načíst detail objednávky';
      set({
        errorDetail: errorMessage,
        isLoadingDetail: false,
      });
    }
  },

  fetchWorkflowActions: async (workflowInstanceId: number) => {
    set({ isLoadingActions: true });

    try {
      const { orgId: organizationId, portalId } = useOrgPortal.getState();

      if (!organizationId || !portalId) {
        throw new Error('Organizace nebo portál nebyl nalezen');
      }

      const response = await workflowApi.getWorkflowTaskActions({
        instanceId: workflowInstanceId,
        organizationId,
        portalId,
      });

      set({
        workflowActions: response.data || [],
        isLoadingActions: false,
      });
    } catch (err) {
      console.error('Error fetching workflow actions:', err);
      set({ workflowActions: [], isLoadingActions: false });
    }
  },

  completeWorkflowAction: async (workflowInstanceId: number, actionId: string, comment?: string) => {
    const { orgId: organizationId, portalId } = useOrgPortal.getState();

    if (!organizationId || !portalId) {
      throw new Error('Organizace nebo portál nebyl nalezen');
    }

    await workflowApi.completeWorkflowTask({
      instanceId: workflowInstanceId,
      organizationId,
      portalId,
      workflowCompleteRequest: {
        actionId,
        comment,
        variables: {},
      },
    });

    // Refresh detail + workflow actions
    const orderDetail = useOrderStore.getState().orderDetail;
    if (orderDetail) {
      await useOrderStore.getState().fetchOrderDetail(orderDetail.id);
    }
  },

  clearDetail: () => {
    set({ orderDetail: null, errorDetail: null, workflowActions: [] });
  },
}));
